import React, { useState, useEffect } from 'react';

const C = {
  bg: '#0a0e27', panel: '#141932', card2: '#1a2045', border: '#252b4a',
  text: '#e4e6ed', muted: '#8b92a8', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', err: '#ef4444', purple: '#a855f7',
};

const TYPE_ICONS = {
  email: '✉️',
  push: '🔔',
  ai_analyzer: '🤖',
  settings: '⚙️',
  deploy: '🚀',
};
const TYPE_COLORS = {
  email: C.accent,
  push: C.purple,
  ai_analyzer: '#a855f7',
  settings: C.warn,
  deploy: C.ok,
};
const TYPE_LABELS = {
  email: 'Email',
  push: 'Push',
  ai_analyzer: 'AI elemzés',
  settings: 'Beállítások',
  deploy: 'Deploy',
};

function severityColor(sev) {
  if (sev === 'error') return C.err;
  if (sev === 'warn') return C.warn;
  return C.muted;
}

function fmtRelative(iso) {
  try {
    const diff = (Date.now() - Date.parse(iso)) / 1000;
    if (diff < 60) return Math.floor(diff) + ' mp';
    if (diff < 3600) return Math.floor(diff / 60) + ' perc';
    if (diff < 86400) return Math.floor(diff / 3600) + ' óra';
    return Math.floor(diff / 86400) + ' nap';
  } catch (e) { return ''; }
}

export default function SystemEvents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const load = async (typeFilter) => {
    setLoading(true); setErr(null);
    try {
      const url = '/api/system-events?limit=300' + (typeFilter ? '&type=' + typeFilter : '');
      const r = await fetch(url, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status);
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  function toggleExpand(idx) {
    const s = new Set(expanded);
    if (s.has(idx)) s.delete(idx); else s.add(idx);
    setExpanded(s);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>📜 Esemény napló</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {data && <span style={{ fontSize: 11, color: C.muted }}>{data.count} esemény {data.totalCount !== data.count ? '(' + data.totalCount + ' összesen)' : ''}</span>}
          <button onClick={() => load(filter)} disabled={loading}
            style={{ padding: '6px 12px', fontSize: 12, background: C.border, color: C.text, border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            {loading ? '…' : '🔄 Frissítés'}
          </button>
        </div>
      </div>

      {/* Filter chip-ek */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setFilter(null)}
          style={{ padding: '6px 12px', fontSize: 12, background: filter === null ? C.accent : C.panel, color: filter === null ? '#000' : C.text, border: '1px solid ' + C.border, borderRadius: 16, cursor: 'pointer' }}>
          Mind {data && '(' + data.totalCount + ')'}
        </button>
        {data && Object.keys(TYPE_LABELS).map(t => (
          <button key={t} onClick={() => setFilter(t)}
            style={{ padding: '6px 12px', fontSize: 12, background: filter === t ? TYPE_COLORS[t] : C.panel, color: filter === t ? '#000' : C.text, border: '1px solid ' + C.border, borderRadius: 16, cursor: 'pointer' }}>
            {TYPE_ICONS[t]} {TYPE_LABELS[t]} {data.stats[t] != null && '(' + data.stats[t] + ')'}
          </button>
        ))}
      </div>

      {err && <div style={{ padding: 12, background: '#3a1f1f', border: '1px solid ' + C.err, borderRadius: 4, color: C.err, marginBottom: 16 }}>⚠️ {err}</div>}

      <div className="card" style={{ padding: 0 }}>
        {data && data.events.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Nincs esemény ezzel a szűrővel.</div>
        )}
        {data && data.events.map((e, i) => {
          const isExpanded = expanded.has(i);
          const color = TYPE_COLORS[e.type] || C.muted;
          return (
            <div key={i} onClick={() => toggleExpand(i)}
              style={{ display: 'flex', padding: '10px 16px', borderBottom: '1px solid ' + C.border, cursor: 'pointer', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: '0 0 auto', width: 6, height: 'auto', background: color, borderRadius: 3, minHeight: 36, alignSelf: 'stretch' }}></div>
              <div style={{ flex: '0 0 auto', minWidth: 110, fontSize: 11, color: C.muted, paddingTop: 2 }}>
                <div style={{ fontWeight: 600, color: severityColor(e.severity) }}>
                  {new Date(e.timestamp).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'medium' })}
                </div>
                <div>{fmtRelative(e.timestamp)} ezelőtt</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: e.severity === 'error' ? 600 : 500, color: e.severity === 'error' ? C.err : C.text }}>
                  {e.title}
                </div>
                {isExpanded && e.details && (
                  e.type === 'settings' && e.details.new_value !== undefined ? (
                    <div style={{ marginTop: 8, padding: 10, background: C.bg, borderRadius: 4, fontSize: 12 }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Kulcs: <code style={{ color: C.accent }}>{e.details.key}</code> · módosította: <b>{e.details.changed_by || 'admin'}</b></div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr', gap: 8, alignItems: 'center' }}>
                        <div style={{ padding: 6, background: '#3a1f1f', border: '1px solid ' + C.err, borderRadius: 4, color: C.err, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word' }}>
                          {e.details.old_value === '' || e.details.old_value == null ? <span style={{ color: C.muted, fontStyle: 'italic' }}>(üres)</span> : e.details.old_value}
                        </div>
                        <div style={{ textAlign: 'center', color: C.accent, fontWeight: 'bold' }}>→</div>
                        <div style={{ padding: 6, background: '#1f3a1f', border: '1px solid ' + C.ok, borderRadius: 4, color: C.ok, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word' }}>
                          {e.details.new_value === '' || e.details.new_value == null ? <span style={{ color: C.muted, fontStyle: 'italic' }}>(üres)</span> : e.details.new_value}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <pre style={{ marginTop: 8, padding: 10, background: C.bg, borderRadius: 4, fontSize: 11, color: C.muted, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>
                      {JSON.stringify(e.details, null, 2)}
                    </pre>
                  )
                )}
              </div>
              <div style={{ flex: '0 0 auto', color: C.muted, fontSize: 11, paddingTop: 4 }}>{isExpanded ? '▾' : '▸'}</div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: C.muted }}>
        💡 Az eseménynapló forrásai: <code>email_history</code>, <code>push_history</code>, <code>daily_costs</code> (AI elemzés), <code>settings</code> (módosítások), és a dashboard process uptime (deploy proxy). Az időbélyegek a SQLite-ban tárolt érték — a deploy a container indítása óta eltelt idő alapján.
      </div>
    </div>
  );
}
